import React from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { DISTINCTIONS, distinctionOf } from "../mock";
import { usePlaces } from "../context/PlacesContext";

export function SectionLabel({ children, className = "" }) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <span className="h-px w-8 bg-claret" />
      <span className="eyebrow text-claret">{children}</span>
    </div>
  );
}

export function ScoreMeter({ label, value }) {
  return (
    <div className="py-3 border-b border-ink/10">
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-sm font-medium">{label}</span>
        <span className="font-serif text-lg font-semibold">{value}</span>
      </div>
      <div className="h-[3px] bg-ink/10 overflow-hidden">
        <div className="h-full bg-claret transition-all duration-700" style={{ width: `${(value / 10) * 100}%` }} />
      </div>
    </div>
  );
}

// Renders 1–3 Placeworthy marks (or nothing for level 0).
export function DistinctionMark({ level, showLabel = true, size = 20, inverted = false, className = "", stack = false }) {
  if (!level) {
    if (!showLabel) return null;
    return <span className={`eyebrow text-[10px] ${inverted ? "text-paper/60" : "text-ink-soft"} ${className}`}>Reviewed</span>;
  }
  const meta = DISTINCTIONS[level];
  const marks = (
    <span className="flex items-center gap-1 shrink-0">
      {Array.from({ length: level }).map((_, i) => (
        <img
          key={i}
          src="/placeworthy-mark.png"
          alt=""
          aria-hidden="true"
          style={{ width: size, height: size }}
          className={inverted ? "invert" : ""}
        />
      ))}
    </span>
  );
  if (!showLabel) return <span className={className}>{marks}</span>;
  return (
    <span className={`inline-flex ${stack ? "flex-col items-start gap-1.5" : "items-center gap-2"} ${className}`}>
      {marks}
      <span className={`font-serif font-semibold leading-none ${inverted ? "text-paper" : "text-ink"}`}>
        {meta.label}
        <span className={`block eyebrow text-[9px] mt-0.5 font-sans ${inverted ? "text-brass" : "text-claret"}`}>{meta.sub}</span>
      </span>
    </span>
  );
}

export function PlaceCard({ place, index = 0 }) {
  const level = distinctionOf(place);
  const meta = DISTINCTIONS[level];
  const { isFav, toggleFav } = usePlaces();
  const fav = isFav(place.slug);
  return (
    <Link
      to={`/place/${place.slug}`}
      className="group block rise-in"
      style={{ animationDelay: `${(index % 6) * 70}ms` }}
    >
      <div className="img-zoom relative aspect-[4/5] bg-ink/5">
        <img src={place.image} alt={place.name} className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent opacity-80" />
        {place.accolades?.length > 0 && (
          <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5 max-w-[70%]">
            {place.accolades.slice(0, 2).map((a) => (
              <span key={a} className="bg-brass text-ink text-[10px] font-semibold tracking-wide uppercase px-2.5 py-1">{a}</span>
            ))}
          </div>
        )}
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav(place.slug); }}
          aria-label={fav ? "Remove from your list" : "Save to your list"}
          className={`absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full backdrop-blur-sm transition-all ${
            fav ? "bg-claret text-paper" : "bg-ink/30 text-paper hover:bg-ink/50"
          }`}
        >
          <Heart className="w-4 h-4" fill={fav ? "currentColor" : "none"} />
        </button>
        {level > 0 && (
          <div className="absolute bottom-4 left-4 flex items-center gap-1">
            {Array.from({ length: level }).map((_, i) => (
              <img key={i} src="/placeworthy-mark.png" alt="" aria-hidden="true" className="invert w-5 h-5" />
            ))}
          </div>
        )}
      </div>
      <div className="pt-4">
        <div className="flex items-center gap-2 text-ink-soft text-xs">
          <span>{place.category}</span>
          <span className="w-1 h-1 rounded-full bg-ink-soft/50" />
          <span>{place.city}</span>
          <span className="ml-auto text-ink-soft/70">{place.price}</span>
        </div>
        <h3 className="font-serif text-2xl font-semibold mt-1.5 group-hover:text-claret transition-colors">{place.name}</h3>
        {level > 0 && (
          <p className="text-[11px] eyebrow text-claret mt-1">{meta.label} · {meta.sub}</p>
        )}
        <p className="text-sm text-ink-soft mt-2 leading-relaxed line-clamp-2">{place.excerpt}</p>
      </div>
    </Link>
  );
}

export function PlaceRow({ place }) {
  const level = distinctionOf(place);
  const meta = DISTINCTIONS[level];
  return (
    <Link
      to={`/place/${place.slug}`}
      className="group flex gap-4 sm:gap-5 py-5 border-b border-ink/10 hover:bg-ink/[0.02] transition-colors px-2 -mx-2"
    >
      <div className="img-zoom w-20 h-20 sm:w-24 sm:h-24 shrink-0 bg-ink/5">
        <img src={place.image} alt={place.name} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="flex-1 min-w-0">
        {level > 0 ? (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="flex items-center gap-1">
              {Array.from({ length: level }).map((_, i) => (
                <img key={i} src="/placeworthy-mark.png" alt="" aria-hidden="true" className="w-4 h-4" />
              ))}
            </span>
            <span className="eyebrow text-[9px] text-claret">{meta.label}</span>
          </div>
        ) : (
          <span className="eyebrow text-[9px] text-ink-soft">Reviewed</span>
        )}
        <h3 className="font-serif text-lg sm:text-xl font-semibold mt-1 leading-tight group-hover:text-claret transition-colors">
          {place.name}
        </h3>
        <div className="flex items-center gap-1.5 text-ink-soft text-xs mt-1">
          <span>{place.category}</span>
          <span className="w-1 h-1 rounded-full bg-ink-soft/50" />
          <span className="truncate">{place.city} · {place.neighborhood}</span>
        </div>
        <p className="hidden sm:block text-sm text-ink-soft mt-1.5 line-clamp-1">{place.excerpt}</p>
      </div>
    </Link>
  );
}
